import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { dictionary } from '../../../dictionary';

const FilteredInvoicesDialog = ({ visible, onHide, data, allInvoices }) => {
  const [maximized, setMaximized] = useState(false);

  const formatValue = (val, type) => {
    if (val === '' || val === null || val === undefined) return '-';
    if (val === 0 || val === '0') {
      if (type === 'EUR') return '0.00 €';
      if (type === 'PLN') return '0.00 PLN';
      return '0';
    }
    if (type === 'EUR' || type === 'PLN') {
      const num = Number(val);
      if (isNaN(num)) return val;
      const formatted = num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return type === 'EUR' ? `${formatted} €` : `${formatted} PLN`;
    }
    return val;
  };

  const excelTableData = useMemo(() => {
    if (!data || !allInvoices) return [];

    const flatRows = [];
    let rowIndex = 1;

    data.forEach((logGroup) => {
      const firstLogId = logGroup.entries?.[0]?._id;
      const inv = allInvoices.find((i) => i.entries?.some((e) => e._id === firstLogId));
      if (!inv) return;

      const convRate = Number(inv.conv);
      const hasConv = !isNaN(convRate) && convRate > 0;

      const calcEur = (plnVal) => {
        const val = Number(plnVal);
        if (isNaN(val) || !hasConv) return 0;
        return val / convRate;
      };

      const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d);
        if (isNaN(date.getTime())) return '-';
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      };

      const baseRow = {
        _gj: inv._computedGJ || logGroup._computedGJ || '-',
        esp: logGroup.esp || '-',
        country: logGroup.country || '-',
        module: logGroup.module || '-',
        _moduleGroup: logGroup.entries?.[0]?.module_type || '-',
        lang: logGroup.lang || '-',
        _version: logGroup.version || '-',
      };

      const hasInv1 =
        inv.dateInvoice1 ||
        inv.payment1Confirmed ||
        Number(inv.netPaid1Invoice) > 0 ||
        (inv.invoiceNumber1 && inv.invoiceNumber1.trim() !== '');

      if (hasInv1) {
        flatRows.push({
          ...baseRow,
          id: rowIndex++,
          invType: '1',
          dateReg: formatDate(inv.dateInvoice1),
          netPaid: formatValue(inv.netPaid1Invoice, 'PLN'),
          netPaidEur: formatValue(calcEur(inv.netPaid1Invoice), 'EUR'),
          grossPaid: formatValue(inv.grossPaid1Invoice, 'PLN'),
          grossPaidEur: formatValue(calcEur(inv.grossPaid1Invoice), 'EUR'),
          invNum: inv.invoiceNumber1 || '-',
          datePayConf:
            formatDate(inv.payment1Confirmed) !== '-'
              ? formatDate(inv.payment1Confirmed)
              : inv.payment1Confirmed === true || inv.payment1Confirmed === 'true'
                ? 'Yes'
                : inv.payment1Confirmed || '-',
        });
      }

      const hasInv2 =
        inv.dateInvoice2 ||
        inv.payment2Confirmed ||
        Number(inv.netPaid2Invoice) > 0 ||
        (inv.invoiceNumber2 && inv.invoiceNumber2.trim() !== '');

      if (hasInv2) {
        flatRows.push({
          ...baseRow,
          id: rowIndex++,
          invType: '2',
          dateReg: formatDate(inv.dateInvoice2),
          netPaid: formatValue(inv.netPaid2Invoice, 'PLN'),
          netPaidEur: formatValue(calcEur(inv.netPaid2Invoice), 'EUR'),
          grossPaid: formatValue(inv.grossPaid2Invoice, 'PLN'),
          grossPaidEur: formatValue(calcEur(inv.grossPaid2Invoice), 'EUR'),
          invNum: inv.invoiceNumber2 || '-',
          datePayConf:
            formatDate(inv.payment2Confirmed) !== '-'
              ? formatDate(inv.payment2Confirmed)
              : inv.payment2Confirmed === true || inv.payment2Confirmed === 'true'
                ? 'Yes'
                : inv.payment2Confirmed || '-',
        });
      }
    });

    return flatRows;
  }, [data, allInvoices]);

  const handleExport = () => {
    const now = new Date();
    const dateStr =
      now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
    const fileName = `filtered_inv_report_${dateStr}`;

    const columns = [
      { field: 'id', header: dictionary.esp_settlements_report_col_id || 'Lp.' },
      { field: '_gj', header: dictionary.esp_settlements_report_col_gj || 'GJ' },
      { field: 'esp', header: dictionary.esp_settlements_report_col_esp || 'ESP' },
      { field: 'country', header: dictionary.esp_settlements_report_col_country || 'Country' },
      { field: 'module', header: dictionary.esp_settlements_report_col_module || 'Module' },
      { field: '_moduleGroup', header: dictionary.esp_settlements_report_col_mod_type || 'Module type' },
      { field: 'lang', header: dictionary.esp_settlements_report_col_lang || 'Language' },
      { field: '_version', header: dictionary.esp_settlements_report_col_version || 'Version' },
      { field: 'invType', header: dictionary.esp_settlements_report_col_inv_type || 'First or second invoice' },
      { field: 'dateReg', header: dictionary.esp_settlements_report_col_date_reg || 'Date of registration' },
      { field: 'netPaid', header: dictionary.esp_settlements_report_col_net_paid || 'Net paid invoice' },
      { field: 'netPaidEur', header: dictionary.esp_settlements_report_col_net_paid_eur || 'Net paid EUR Invoice' },
      { field: 'grossPaid', header: dictionary.esp_settlements_report_col_gross_paid || 'Gross paid Invoice' },
      {
        field: 'grossPaidEur',
        header: dictionary.esp_settlements_report_col_gross_paid_eur || 'Gross paid EUR Invoice',
      },
      { field: 'invNum', header: dictionary.esp_settlements_report_col_inv_num || 'Invoice number' },
      { field: 'datePayConf', header: dictionary.esp_settlements_report_col_date_conf || 'Date payment confirmed' },
    ];

    let mdText = `# ${fileName}\n\nData:\n\n`;
    mdText += '| ' + columns.map((c) => c.header).join(' | ') + ' |\n';
    mdText += '|' + columns.map(() => ' :--- ').join('|') + '|\n';

    excelTableData.forEach((row) => {
      mdText +=
        '| ' +
        columns
          .map((c) => {
            let val = row[c.field];
            if (val === null || val === undefined) val = '';
            return String(val).replace(/\|/g, '').replace(/\n/g, ' ');
          })
          .join(' | ') +
        ' |\n';
    });

    navigator.clipboard
      .writeText(mdText)
      .then(() => {
        const gemUrl = 'https://gemini.google.com/gem/16HBo00jKmoq8NCb4AKE1p3DViq8LKVyQ?usp=sharing';
        window.open(gemUrl, '_blank');
        alert(
          `Data copied to clipboard!\n\nTarget filename: ${fileName}\n\nPaste the data (Ctrl+V) into Gemini to generate the report.`
        );
      })
      .catch((err) => console.error('Copy error:', err));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={dictionary.esp_filtered_export_btn || 'Export to Sheets'}
        icon="pi pi-external-link"
        onClick={handleExport}
        className="p-button-success"
      />
    </div>
  );

  return (
    <Dialog
      header={dictionary.esp_dialog_invoice_filtered_fv || 'FILTERED INVOICES'}
      visible={visible}
      onHide={onHide}
      footer={footer}
      maximizable
      maximized={maximized}
      onMaximize={(e) => setMaximized(e.maximized)}
      style={{ width: '90vw' }}
      className="esp-report-dialog"
    >
      <DataTable
        value={excelTableData}
        size="small"
        stripedRows
        scrollable
        scrollHeight="calc(100vh - 200px)"
        className="filtered-records-table"
        showGridlines
        paginator
        rows={25}
      >
        <Column
          field="id"
          header={dictionary.esp_settlements_report_col_id || 'Lp.'}
          frozen
          alignFrozen="left"
          style={{ width: '3rem' }}
        />
        <Column field="_gj" header={dictionary.esp_settlements_report_col_gj || 'GJ'} frozen alignFrozen="left" />
        <Column field="esp" header={dictionary.esp_settlements_report_col_esp || 'ESP'} frozen alignFrozen="left" />
        <Column field="country" header={dictionary.esp_settlements_report_col_country || 'Country'} />
        <Column field="module" header={dictionary.esp_settlements_report_col_module || 'Module'} />
        <Column field="_moduleGroup" header={dictionary.esp_settlements_report_col_mod_type || 'Module type'} />
        <Column field="lang" header={dictionary.esp_settlements_report_col_lang || 'Language'} />
        <Column field="_version" header={dictionary.esp_settlements_report_col_version || 'Version'} />
        <Column field="invType" header={dictionary.esp_settlements_report_col_inv_type || 'First or second invoice'} />
        <Column field="dateReg" header={dictionary.esp_settlements_report_col_date_reg || 'Date of registration'} />
        <Column field="netPaid" header={dictionary.esp_settlements_report_col_net_paid || 'Net paid invoice'} />
        <Column
          field="netPaidEur"
          header={dictionary.esp_settlements_report_col_net_paid_eur || 'Net paid EUR Invoice'}
        />
        <Column field="grossPaid" header={dictionary.esp_settlements_report_col_gross_paid || 'Gross paid Invoice'} />
        <Column
          field="grossPaidEur"
          header={dictionary.esp_settlements_report_col_gross_paid_eur || 'Gross paid EUR Invoice'}
        />
        <Column field="invNum" header={dictionary.esp_settlements_report_col_inv_num || 'Invoice number'} />
        <Column
          field="datePayConf"
          header={dictionary.esp_settlements_report_col_date_conf || 'Date payment confirmed'}
        />
      </DataTable>
    </Dialog>
  );
};

export default FilteredInvoicesDialog;
